import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, Trophy, TrendingUp, Clock, Users, CheckCircle,
  AlertTriangle, Loader2, BarChart3, ChevronRight, Award,
  Crown, Medal, Shield, Zap, Lock, Timer, Activity,
  Flame, Sun, CalendarDays
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  region_loan_requests: { icon: BarChart3, color: 'from-blue-500 to-cyan-400', label: 'Regional Activity' },
  lending_volume: { icon: TrendingUp, color: 'from-green-500 to-emerald-400', label: 'Lending Volume' },
  stablecoin_popularity: { icon: Zap, color: 'from-purple-500 to-pink-400', label: 'Stablecoin Trends' },
  business_ar_financing: { icon: Activity, color: 'from-orange-500 to-amber-400', label: 'Business Finance' },
};

function CountdownTimer({ endDate }) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const tick = () => {
      const end = new Date(endDate);
      const now = new Date();
      const diff = end - now;
      if (diff <= 0) { setRemaining('Ended'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${d}d ${h}h ${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [endDate]);
  return <span data-testid="countdown-timer">{remaining}</span>;
}

function MarketCard({ market, onPredict, userPrediction, isAuthenticated }) {
  const meta = CATEGORY_META[market.category] || CATEGORY_META.region_loan_requests;
  const Icon = meta.icon;
  const breakdown = market.choice_breakdown || {};
  const totalPredictions = market.prediction_count || 0;
  const isResolved = market.status === 'resolved';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        className="bg-[#0D0D0D] border-white/5 hover:border-white/10 transition-all group"
        data-testid={`market-card-${market.market_id}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center shrink-0`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <Badge variant="outline" className="text-[10px] mb-1 border-white/10 text-gray-500">
                  {meta.label}
                </Badge>
                <CardTitle className="text-base text-white leading-tight">{market.title}</CardTitle>
              </div>
            </div>
            <Badge
              className={`shrink-0 ${
                isResolved ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'
              }`}
              data-testid={`market-status-${market.market_id}`}
            >
              {isResolved ? 'Resolved' : 'Active'}
            </Badge>
          </div>
          {market.description && (
            <CardDescription className="text-gray-500 text-xs mt-2">{market.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Choices breakdown */}
          <div className="space-y-2" data-testid={`market-choices-${market.market_id}`}>
            {market.choices.map((choice) => {
              const data = breakdown[choice] || { count: 0, total_stake: 0 };
              const pct = totalPredictions > 0 ? Math.round((data.count / totalPredictions) * 100) : 0;
              const isWinner = isResolved && (
                market.winning_choice === choice ||
                (market.tied_choices?.length > 1 && market.tied_choices.includes(choice))
              );
              const isUserPick = userPrediction?.choice === choice;
              return (
                <div
                  key={choice}
                  className={`relative rounded-lg p-3 border transition-all ${
                    isWinner ? 'border-green-500/30 bg-green-500/5' :
                    isUserPick ? 'border-blue-500/30 bg-blue-500/5' :
                    'border-white/5 bg-white/[0.02]'
                  }`}
                  data-testid={`choice-${market.market_id}-${choice}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white flex items-center gap-2">
                      {choice}
                      {isWinner && <CheckCircle className="w-3.5 h-3.5 text-green-400" />}
                      {isUserPick && <Badge className="text-[9px] px-1.5 py-0 bg-blue-500/20 text-blue-400">Your pick</Badge>}
                    </span>
                    <span className="text-xs text-gray-500">{data.count} votes | {pct}%</span>
                  </div>
                  <Progress value={pct} className="h-1.5 bg-white/5" />
                </div>
              );
            })}
          </div>

          {/* Meta row */}
          <div className="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-white/5">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{totalPredictions} predictions</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{market.stake_amount} KOJIRU stake</span>
            </div>
            {!isResolved && (
              <span className="flex items-center gap-1 text-orange-400">
                <Timer className="w-3 h-3" /><CountdownTimer endDate={market.period_end} />
              </span>
            )}
          </div>

          {/* Pool info */}
          <div className="flex items-center gap-2 bg-white/[0.02] rounded-lg p-2.5 border border-white/5">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-gray-400">
              Reward Pool: <span className="text-white font-medium">{(market.total_staked || 0) + (market.platform_bonus || 0)} KOJIRU</span>
              <span className="text-gray-600 ml-1">(+{market.platform_bonus || 0} bonus)</span>
            </span>
          </div>

          {/* Resolution info */}
          {isResolved && (
            <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3" data-testid={`market-result-${market.market_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-green-400">
                  {market.tied_choices?.length > 1
                    ? `Pot Split: ${market.tied_choices.join(' & ')}`
                    : `Winner: ${market.winning_choice}`}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {market.total_winners || 0} winners | {market.payout_per_winner || 0} KOJIRU each
                {market.flagged_count > 0 && (
                  <span className="text-orange-400 ml-2">| {market.flagged_count} flagged</span>
                )}
              </div>
              {market.tiebreaker_used && (
                <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-blue-400">
                  <Shield className="w-3 h-3" />
                  {market.tiebreaker_used === 'earliest_activity'
                    ? 'Tie resolved by earliest verified activity'
                    : 'Identical tie — pot split across all tied choices'}
                </div>
              )}
              {market.resolution_data?.tiebreaker && (
                <p className="text-[10px] text-gray-600 mt-1">{market.resolution_data.tiebreaker}</p>
              )}
            </div>
          )}

          {/* Action button */}
          {!isResolved && !userPrediction && isAuthenticated && (
            <Button
              onClick={() => onPredict(market)}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90"
              data-testid={`predict-btn-${market.market_id}`}
            >
              <Target className="w-4 h-4 mr-2" />Place Prediction
            </Button>
          )}
          {!isAuthenticated && !isResolved && (
            <Button variant="outline" className="w-full border-white/10" disabled>
              <Lock className="w-4 h-4 mr-2" />Sign in to predict
            </Button>
          )}
          {userPrediction && !isResolved && (
            <div className="text-center text-xs text-gray-500 py-1">
              You predicted <span className="text-blue-400 font-medium">{userPrediction.choice}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function LeaderboardRow({ entry, index }) {
  const badges = [
    <Crown key="c" className="w-5 h-5 text-yellow-400" />,
    <Medal key="m" className="w-5 h-5 text-gray-300" />,
    <Award key="a" className="w-5 h-5 text-orange-400" />,
  ];
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all"
      data-testid={`leaderboard-row-${entry.rank}`}
    >
      <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
        {index < 3 ? badges[index] : <span className="text-sm font-bold text-gray-500">#{entry.rank}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{entry.name || 'Anonymous'}</p>
        <p className="text-xs text-gray-500">{entry.wins} win{entry.wins !== 1 ? 's' : ''}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-green-400">{entry.total_payout.toLocaleString()} KOJIRU</p>
      </div>
    </motion.div>
  );
}

function MyPredictionCard({ pred }) {
  const statusMap = {
    active: { color: 'bg-blue-500/10 text-blue-400', label: 'Active' },
    won: { color: 'bg-green-500/10 text-green-400', label: 'Won' },
    lost: { color: 'bg-red-500/10 text-red-400', label: 'Lost' },
    held: { color: 'bg-orange-500/10 text-orange-400', label: 'Under Review' },
  };
  const st = statusMap[pred.status] || statusMap.active;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`my-prediction-${pred.prediction_id}`}>
      <div>
        <p className="text-sm text-white font-medium">{pred.choice}</p>
        <p className="text-xs text-gray-500">Staked: {pred.stake_amount} KOJIRU</p>
      </div>
      <div className="text-right">
        <Badge className={st.color}>{st.label}</Badge>
        {pred.payout > 0 && <p className="text-xs text-green-400 mt-1">+{pred.payout} KOJIRU</p>}
      </div>
    </div>
  );
}

function DailyChallengeCard({ challenge, streak, onPredict, userPrediction, isAuthenticated, isAdmin, onGenerate, onResolve }) {
  if (!challenge && !isAdmin) return null;

  const breakdown = challenge?.choice_breakdown || {};
  const totalPreds = challenge?.prediction_count || 0;
  const isResolved = challenge?.status === 'resolved';
  const mult = streak?.multiplier || 1.0;
  const currentStreak = streak?.current_streak || 0;
  const nextAt = streak?.next_multiplier_at;

  return (
    <Card className="bg-gradient-to-br from-[#0D0D0D] to-[#111] border-orange-500/20 relative overflow-hidden" data-testid="daily-challenge-card">
      {/* Accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-yellow-500 to-red-500" />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shrink-0">
              <Sun className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-white">Daily Challenge</h3>
                {challenge && (
                  <Badge className={isResolved ? 'bg-green-500/10 text-green-400' : 'bg-orange-500/10 text-orange-400'}>
                    {isResolved ? 'Resolved' : 'Live'}
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-gray-500">Predict daily. Build your streak. Earn more.</p>
            </div>
          </div>
          {/* Streak display */}
          {isAuthenticated && (
            <div className="flex items-center gap-2 bg-white/[0.03] rounded-lg px-3 py-2 border border-white/5" data-testid="streak-display">
              <Flame className={`w-4 h-4 ${currentStreak > 0 ? 'text-orange-400' : 'text-gray-600'}`} />
              <div className="text-right">
                <p className="text-sm font-bold text-white">{currentStreak}</p>
                <p className="text-[10px] text-gray-500">streak</p>
              </div>
              {mult > 1 && (
                <Badge className="bg-orange-500/10 text-orange-400 text-[10px] ml-1">{mult}x</Badge>
              )}
            </div>
          )}
        </div>

        {!challenge ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">No daily challenge yet today.</p>
            {isAdmin && (
              <Button size="sm" onClick={onGenerate} className="bg-gradient-to-r from-orange-600 to-red-600" data-testid="generate-daily-btn">
                <Sun className="w-4 h-4 mr-2" />Generate Today's Challenge
              </Button>
            )}
          </div>
        ) : (
          <>
            <p className="text-base font-medium text-white mb-3" data-testid="daily-question">{challenge.question}</p>

            {/* Choice bars */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {challenge.choices.map(choice => {
                const count = breakdown[choice] || 0;
                const pct = totalPreds > 0 ? Math.round((count / totalPreds) * 100) : 0;
                const isWinner = isResolved && challenge.winning_choice === choice;
                const isUserPick = userPrediction?.choice === choice;
                return (
                  <div
                    key={choice}
                    className={`rounded-lg p-3 border text-center transition-all ${
                      isWinner ? 'border-green-500/30 bg-green-500/5' :
                      isUserPick ? 'border-orange-500/30 bg-orange-500/5' :
                      'border-white/5 bg-white/[0.02]'
                    }`}
                    data-testid={`daily-choice-display-${choice}`}
                  >
                    <span className="text-sm font-medium text-white flex items-center justify-center gap-1">
                      {choice}
                      {isWinner && <CheckCircle className="w-3.5 h-3.5 text-green-400" />}
                      {isUserPick && !isResolved && <Badge className="text-[9px] px-1 py-0 bg-orange-500/20 text-orange-400 ml-1">You</Badge>}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">{count} votes ({pct}%)</p>
                  </div>
                );
              })}
            </div>

            {/* Meta */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <span>{totalPreds} predictions | {challenge.stake_amount} KOJIRU stake</span>
              <span className="text-orange-400">{(challenge.total_staked || 0) + (challenge.platform_bonus || 0)} KOJIRU pool</span>
            </div>

            {/* Streak multiplier info */}
            {isAuthenticated && mult > 1 && !isResolved && (
              <div className="flex items-center gap-2 bg-orange-500/5 border border-orange-500/10 rounded-lg p-2 mb-3 text-xs">
                <Flame className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                <span className="text-orange-300">Your {currentStreak}-day streak gives you a <span className="font-bold text-orange-400">{mult}x payout multiplier</span>!</span>
              </div>
            )}
            {isAuthenticated && nextAt && !isResolved && mult === 1 && (
              <div className="flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-lg p-2 mb-3 text-xs">
                <Flame className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <span className="text-gray-500">Win {nextAt - currentStreak} more in a row to unlock the {_get_streak_multiplier(nextAt)}x multiplier!</span>
              </div>
            )}

            {/* Resolution info */}
            {isResolved && (
              <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3 mb-3" data-testid="daily-result">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-green-400">
                    Answer: {challenge.winning_choice} ({challenge.actual_count} actual)
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {challenge.total_winners || 0} winners | {challenge.payout_per_winner || 0} KOJIRU base payout
                </p>
              </div>
            )}

            {/* Action */}
            {!isResolved && !userPrediction && isAuthenticated && (
              <div className="grid grid-cols-2 gap-2">
                {challenge.choices.map(choice => (
                  <Button
                    key={choice}
                    onClick={() => onPredict(choice)}
                    variant={choice === 'Yes' ? 'default' : 'outline'}
                    className={choice === 'Yes'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:opacity-90'
                      : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                    }
                    data-testid={`daily-predict-${choice.toLowerCase()}`}
                  >
                    {choice}
                  </Button>
                ))}
              </div>
            )}
            {!isAuthenticated && !isResolved && (
              <Button variant="outline" className="w-full border-white/10" disabled>
                <Lock className="w-4 h-4 mr-2" />Sign in to predict
              </Button>
            )}
            {userPrediction && !isResolved && (
              <p className="text-center text-xs text-gray-500 py-1">
                You predicted <span className="text-orange-400 font-medium">{userPrediction.choice}</span> — results at midnight UTC
              </p>
            )}

            {/* Admin resolve */}
            {isAdmin && !isResolved && (
              <Button size="sm" variant="outline" onClick={onResolve} className="w-full mt-2 border-orange-500/30 text-orange-400 hover:bg-orange-500/10" data-testid="resolve-daily-btn">
                Resolve Challenge
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// helper for streak multiplier display in the component
function _get_streak_multiplier(streak) {
  const map = { 0: 1.0, 3: 1.25, 5: 1.5, 7: 1.75, 10: 2.0, 15: 2.5, 20: 3.0 };
  let mult = 1.0;
  for (const [t, m] of Object.entries(map).sort((a, b) => a[0] - b[0])) {
    if (streak >= Number(t)) mult = m;
  }
  return mult;
}

function PredictContent() {
  const { isAuthenticated, token, user } = useAuth();
  const { toast } = useToast();
  const [markets, setMarkets] = useState([]);
  const [myPredictions, setMyPredictions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedChoice, setSelectedChoice] = useState('');
  const [placing, setPlacing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('markets');
  const isAdmin = user?.role === 'admin';
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [dailyStreak, setDailyStreak] = useState(null);
  const [dailyPrediction, setDailyPrediction] = useState(null);
  const [dailyHistory, setDailyHistory] = useState([]);

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [marketsRes, leaderRes, dailyRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/predictions/markets`),
        axios.get(`${API}/api/predictions/leaderboard`),
        axios.get(`${API}/api/predictions/daily`),
        axios.get(`${API}/api/predictions/daily/history?limit=7`),
      ]);
      setMarkets(marketsRes.data);
      setLeaderboard(leaderRes.data);
      setDailyChallenge(dailyRes.data.challenge || null);
      setDailyHistory(historyRes.data || []);

      if (token) {
        const [myRes, streakRes, dailyMyRes] = await Promise.all([
          axios.get(`${API}/api/predictions/my`, { headers: authHeaders() }),
          axios.get(`${API}/api/predictions/daily/streak`, { headers: authHeaders() }),
          axios.get(`${API}/api/predictions/daily/my`, { headers: authHeaders() }),
        ]);
        setMyPredictions(myRes.data.predictions || []);
        setDailyStreak(streakRes.data);
        setDailyPrediction(dailyMyRes.data.prediction || null);
      }
    } catch (e) { console.error('Failed to load data', e); }
    setLoading(false);
  }, [token, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePredict = (market) => {
    setSelectedMarket(market);
    setSelectedChoice('');
    setDialogOpen(true);
  };

  const submitPrediction = async () => {
    if (!selectedChoice || !selectedMarket) return;
    setPlacing(true);
    try {
      await axios.post(
        `${API}/api/predictions/markets/${selectedMarket.market_id}/predict`,
        { choice: selectedChoice },
        { headers: authHeaders() }
      );
      toast({ title: 'Prediction placed!', description: `You picked "${selectedChoice}". Good luck!` });
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not place prediction', variant: 'destructive' });
    } finally {
      setPlacing(false);
    }
  };

  const seedMarkets = async () => {
    try {
      const res = await axios.post(`${API}/api/predictions/seed`, {}, { headers: authHeaders() });
      toast({ title: 'Markets seeded', description: `${res.data.created} new markets created for ${res.data.month}` });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to seed markets', variant: 'destructive' });
    }
  };

  const resolveMarket = async (marketId) => {
    try {
      const res = await axios.post(`${API}/api/predictions/markets/${marketId}/resolve`, {}, { headers: authHeaders() });
      toast({
        title: 'Market resolved',
        description: `Winner: ${res.data.winning_choice} | ${res.data.total_winners} winners get ${res.data.payout_per_winner} KOJIRU each`
      });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to resolve', variant: 'destructive' });
    }
  };

  const generateDaily = async () => {
    try {
      await axios.post(`${API}/api/predictions/daily/generate`, {}, { headers: authHeaders() });
      toast({ title: 'Daily challenge created!' });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to generate', variant: 'destructive' });
    }
  };

  const placeDailyPrediction = async (choice) => {
    try {
      await axios.post(`${API}/api/predictions/daily/predict`, { choice }, { headers: authHeaders() });
      toast({ title: 'Daily prediction placed!', description: `You picked "${choice}". Results at midnight UTC!` });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not place prediction', variant: 'destructive' });
    }
  };

  const resolveDaily = async () => {
    try {
      const res = await axios.post(`${API}/api/predictions/daily/resolve`, {}, { headers: authHeaders() });
      toast({
        title: 'Daily challenge resolved!',
        description: `Answer: ${res.data.winning_choice} (${res.data.actual_count} actual) | ${res.data.total_winners} winners`
      });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to resolve', variant: 'destructive' });
    }
  };

  const userPredMap = {};
  myPredictions.forEach(p => { userPredMap[p.market_id] = p; });
  const activeMarkets = markets.filter(m => m.status === 'active');
  const resolvedMarkets = markets.filter(m => m.status === 'resolved');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="predict-loading">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="predict-page">
      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active Markets', value: activeMarkets.length, icon: Target, color: 'text-blue-400' },
          { label: 'Total Predictions', value: markets.reduce((s, m) => s + (m.prediction_count || 0), 0), icon: Users, color: 'text-purple-400' },
          { label: 'Prize Pool', value: `${markets.filter(m => m.status === 'active').reduce((s, m) => s + (m.total_staked || 0) + (m.platform_bonus || 0), 0).toLocaleString()} KOJIRU`, icon: Trophy, color: 'text-yellow-400' },
          { label: 'Your Wins', value: myPredictions.filter(p => p.status === 'won').length, icon: Award, color: 'text-green-400' },
        ].map((stat) => (
          <Card key={stat.label} className="bg-[#0D0D0D] border-white/5" data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
              <div>
                <p className="text-xs text-gray-500">{stat.label}</p>
                <p className="text-lg font-bold text-white">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Admin controls */}
      {isAdmin && (
        <Card className="bg-orange-500/5 border-orange-500/20" data-testid="admin-controls">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-orange-400 font-medium">Admin Controls</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={seedMarkets} className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10" data-testid="seed-markets-btn">
                Seed Monthly Markets
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Challenge */}
      <DailyChallengeCard
        challenge={dailyChallenge}
        streak={dailyStreak}
        onPredict={placeDailyPrediction}
        userPrediction={dailyPrediction}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        onGenerate={generateDaily}
        onResolve={resolveDaily}
      />

      {/* Daily History */}
      {dailyHistory.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="daily-history">
          {dailyHistory.map(ch => (
            <div key={ch.challenge_id} className="shrink-0 bg-white/[0.02] border border-white/5 rounded-lg p-2.5 min-w-[180px]">
              <div className="flex items-center gap-1.5 mb-1">
                <CalendarDays className="w-3 h-3 text-gray-500" />
                <span className="text-[10px] text-gray-500">{new Date(ch.day_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <Badge className="bg-green-500/10 text-green-400 text-[9px] ml-auto">{ch.winning_choice}</Badge>
              </div>
              <p className="text-[11px] text-gray-400 line-clamp-2">{ch.question}</p>
              <p className="text-[10px] text-gray-600 mt-1">{ch.total_winners || 0}W / {ch.actual_count} actual</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0D0D0D] border border-white/5">
          <TabsTrigger value="markets" data-testid="tab-markets">Markets</TabsTrigger>
          {isAuthenticated && <TabsTrigger value="my" data-testid="tab-my-predictions">My Predictions</TabsTrigger>}
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>
          {resolvedMarkets.length > 0 && <TabsTrigger value="resolved" data-testid="tab-resolved">Results</TabsTrigger>}
        </TabsList>

        {/* Active Markets */}
        <TabsContent value="markets" className="mt-4">
          {activeMarkets.length === 0 ? (
            <Card className="bg-[#0D0D0D] border-white/5">
              <CardContent className="p-12 text-center">
                <Target className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No active markets right now.</p>
                <p className="text-gray-600 text-xs mt-1">Check back soon or ask an admin to seed new markets.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {activeMarkets.map((m) => (
                <MarketCard
                  key={m.market_id}
                  market={m}
                  onPredict={handlePredict}
                  userPrediction={userPredMap[m.market_id]}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* My Predictions */}
        <TabsContent value="my" className="mt-4">
          {myPredictions.length === 0 ? (
            <Card className="bg-[#0D0D0D] border-white/5">
              <CardContent className="p-12 text-center">
                <Target className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">You haven't placed any predictions yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {myPredictions.map(p => <MyPredictionCard key={p.prediction_id} pred={p} />)}
            </div>
          )}
        </TabsContent>

        {/* Leaderboard */}
        <TabsContent value="leaderboard" className="mt-4">
          {leaderboard.length === 0 ? (
            <Card className="bg-[#0D0D0D] border-white/5">
              <CardContent className="p-12 text-center">
                <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No leaderboard data yet. Start predicting!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, i) => <LeaderboardRow key={entry.user_id} entry={entry} index={i} />)}
            </div>
          )}
        </TabsContent>

        {/* Resolved */}
        <TabsContent value="resolved" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {resolvedMarkets.map((m) => (
              <MarketCard
                key={m.market_id}
                market={m}
                onPredict={() => {}}
                userPrediction={userPredMap[m.market_id]}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </div>
          {/* Admin resolve buttons for active markets */}
          {isAdmin && activeMarkets.length > 0 && (
            <Card className="bg-[#0D0D0D] border-white/5 mt-4">
              <CardHeader><CardTitle className="text-sm text-gray-400">Resolve Active Markets</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {activeMarkets.map(m => (
                  <div key={m.market_id} className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/5">
                    <span className="text-sm text-gray-300 truncate mr-3">{m.title}</span>
                    <Button size="sm" variant="outline" onClick={() => resolveMarket(m.market_id)} className="shrink-0 border-green-500/30 text-green-400 hover:bg-green-500/10" data-testid={`resolve-btn-${m.market_id}`}>
                      Resolve
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Safeguards info */}
      <Card className="bg-[#0D0D0D] border-white/5" data-testid="safeguards-info">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-white">5-Layer Anti-Manipulation Protection</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {[
              { n: 'L1', t: 'Verified Data', d: 'Only real activity counts' },
              { n: 'L2', t: 'Eligibility Gate', d: '7-day + 1 transaction' },
              { n: 'L3', t: 'Cross-Activity', d: 'Self-influence detection' },
              { n: 'L4', t: 'Penalties', d: 'Hold, suspend, ban' },
              { n: 'L5', t: 'Anomaly Scan', d: 'Statistical analysis' },
            ].map(l => (
              <div key={l.n} className="bg-white/[0.02] rounded-lg p-2.5 border border-white/5 text-center">
                <Badge className="bg-blue-500/10 text-blue-400 text-[10px] mb-1">{l.n}</Badge>
                <p className="text-xs font-medium text-white">{l.t}</p>
                <p className="text-[10px] text-gray-500">{l.d}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Prediction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0D0D0D] border-white/10 max-w-md" data-testid="predict-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Place Your Prediction</DialogTitle>
            <DialogDescription className="text-gray-500">{selectedMarket?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {selectedMarket?.choices.map(choice => (
              <button
                key={choice}
                onClick={() => setSelectedChoice(choice)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedChoice === choice
                    ? 'border-blue-500 bg-blue-500/10 text-white'
                    : 'border-white/5 bg-white/[0.02] text-gray-400 hover:border-white/15'
                }`}
                data-testid={`dialog-choice-${choice}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{choice}</span>
                  {selectedChoice === choice && <CheckCircle className="w-4 h-4 text-blue-400" />}
                </div>
              </button>
            ))}
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-white/[0.02] p-2.5 rounded-lg border border-white/5">
              <AlertTriangle className="w-3.5 h-3.5 text-orange-400 shrink-0" />
              <span>Stake: <span className="text-white font-medium">{selectedMarket?.stake_amount} KOJIRU</span>. One prediction per market. Cannot be changed.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="cancel-predict-btn">Cancel</Button>
            <Button
              onClick={submitPrediction}
              disabled={!selectedChoice || placing}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              data-testid="confirm-predict-btn"
            >
              {placing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
              Confirm Prediction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PredictPage() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <DashboardLayout title="Kojiru Predict">
        <PredictContent />
      </DashboardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="pt-24 pb-16 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-3" data-testid="predict-hero-title">
            Kojiru Predict
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-base">
            Predict platform trends, earn KOJIRU rewards. Powered by real on-chain data with 5-layer anti-manipulation protection.
          </p>
        </div>
        <PredictContent />
      </div>
      <Footer />
    </div>
  );
}
